import { useMutation } from '@base-app/web2'
import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CREATE_VIDEO_PLAYLIST,
  UPDATE_VIDEO_PLAYLIST,
} from '../../../graphql/videoPlaylists/mutations'
import { VIDEO_PLAYLIST_QUERY } from '../../../graphql/videoPlaylists/queries'
import type { VideoPlaylist } from '../../../graphql/videoPlaylists/types'
import { useRecordById } from '../../../hooks/useRecordById'
import { useToast } from '../../../hooks/useToast'
import { prependToLists } from '../../../lib/cacheLists'
import { Button, FormActions } from '../../atoms'
import { FormField, FormLoadState, TextareaField } from '../../molecules'

interface VideoPlaylistFormFieldsProps {
  playlist?: VideoPlaylist
  clientId: string
  appId: string
  onSuccess: () => void
  onClose: () => void
  // Renders every field as its pulsing placeholder while the record loads
  skeleton?: boolean
}

export const VideoPlaylistFormFields = ({
  playlist,
  clientId,
  appId,
  onSuccess,
  onClose,
  skeleton,
}: VideoPlaylistFormFieldsProps) => {
  const { t } = useTranslation('admin-mobifull')
  const { showToast } = useToast()
  const isEdit = !!playlist
  const [name, setName] = useState(playlist?.name ?? '')
  const [description, setDescription] = useState(playlist?.description ?? '')
  const [observations, setObservations] = useState(playlist?.observations ?? '')
  const [errors, setErrors] = useState<{ name?: string }>({})

  const makeOptimisticPlaylist = (id: string): VideoPlaylist => ({
    __typename: 'VideoPlaylist',
    id,
    client_id: clientId,
    application_id: appId,
    name,
    description: description || null,
    observations: observations || null,
    thumbnail: null,
    synchronized_at: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  })

  const [createPlaylist, { loading: creating }] = useMutation(CREATE_VIDEO_PLAYLIST, {
    optimisticResponse: { createVideoPlaylist: makeOptimisticPlaylist(`temp-${Date.now()}`) },
    update(cache, result) {
      const newPlaylist = result.data?.createVideoPlaylist
      if (!newPlaylist) {
        return
      }
      prependToLists(cache, 'videoPlaylists', newPlaylist, { clientId, applicationId: appId })
    },
    onError(err) {
      showToast(err.message, 'error')
    },
    onCompleted() {
      onSuccess()
    },
  })

  const [updatePlaylist, { loading: updating }] = useMutation(UPDATE_VIDEO_PLAYLIST, {
    optimisticResponse: { updateVideoPlaylist: makeOptimisticPlaylist(playlist?.id ?? '') },
    onError(err) {
      showToast(err.message, 'error')
    },
    onCompleted() {
      onSuccess()
    },
  })

  const loading = creating || updating

  function validate() {
    const errs: { name?: string } = {}
    if (!name.trim()) {
      errs.name = t('common.form.nameRequired')
    }
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) {
      return
    }
    const vars = {
      name,
      description: description || null,
      observations: observations || null,
    }
    if (isEdit) {
      updatePlaylist({ variables: { id: playlist.id, ...vars } })
    } else {
      createPlaylist({ variables: { client_id: clientId, application_id: appId, ...vars } })
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate aria-busy={skeleton}>
      {skeleton ? (
        <p className="sr-only" role="status">
          {t('common.loading')}
        </p>
      ) : null}
      <FormField
        skeleton={skeleton}
        id="playlist-name"
        label={t('videoPlaylists.form.nameLabel')}
        name="name"
        value={name}
        onChange={e => setName(e.target.value)}
        error={errors.name}
        required
      />
      <TextareaField
        skeleton={skeleton}
        id="playlist-description"
        label={t('videoPlaylists.form.descriptionLabel')}
        name="description"
        value={description ?? ''}
        onChange={e => setDescription(e.target.value)}
      />
      <TextareaField
        skeleton={skeleton}
        id="playlist-observations"
        label={t('videoPlaylists.form.observationsLabel')}
        name="observations"
        value={observations ?? ''}
        onChange={e => setObservations(e.target.value)}
      />
      <FormActions>
        <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
          {t('common.actions.cancel')}
        </Button>
        <Button skeleton={skeleton} type="submit" disabled={loading}>
          {loading ? t('videoPlaylists.form.saving') : t('videoPlaylists.form.save')}
        </Button>
      </FormActions>
    </form>
  )
}

interface VideoPlaylistFormProps extends Omit<VideoPlaylistFormFieldsProps, 'playlist'> {
  // The record to edit, by id; omit it to create a new one
  id?: string
}

// Loads the record by id (from the cache when the list has it, otherwise from the API)
export const VideoPlaylistForm = memo(({ id, ...props }: VideoPlaylistFormProps) => {
  const { record, status } = useRecordById(VIDEO_PLAYLIST_QUERY, id, data => data.videoPlaylist)
  if (status === 'loading') {
    return <VideoPlaylistFormFields skeleton {...props} />
  }
  if (status !== 'new' && status !== 'ready') {
    return <FormLoadState status={status} onClose={props.onClose} />
  }
  return <VideoPlaylistFormFields key={id ?? 'new'} playlist={record} {...props} />
})

VideoPlaylistForm.displayName = 'VideoPlaylistForm'
